import { Close, Edit } from '@mui/icons-material'
import {
  Card,
  Container,
  Dialog,
  Divider,
  Grid,
  TextField,
  Typography,
  DialogTitle,
  IconButton,
  CardContent,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Select,
  Box,
  Chip,
  FormControl,
  CircularProgress
} from '@mui/material'

import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { ActionButton } from '../../../utils/ui_utils'
import OTPInput, { ResendOTP } from 'otp-input-react'
import {
  getUserNameFromToken,
  getUserType
} from '../../../components/AuthGuard'
import InputAdornment from '@mui/material/InputAdornment'
import { useFetchOutlet } from '../../../modules/outlet/outletHooks'

function UserDialog({
  visible,
  onSubmit,
  onClose,
  initialValues,
  okText,
  title,
  mode = 'new',
  showOTPui,
  onResendOTP,
  onMobileEdit,
  forFranchise = false,
  showOutletSelection = true,
  loading = false,
  outletName = ''
}) {
  if (initialValues == undefined) {
    initialValues = {
      first_name: '',
      last_name: '',
      user_name: '',
      mobile: '',
      user_type: '',
      email: '',
      password: '',
      confirmPassword: '',
      address: '',
      role: ''
    }
  }
  initialValues.user_type =
    forFranchise && mode === 'new' ? 'FOwner' : initialValues.user_type
  if (initialValues.password == undefined) initialValues.password = ''
  if (initialValues.confirmPassword == undefined)
    initialValues.confirmPassword = ''

  let validationSchema = {}
  const [OTP, setOTP] = useState('')

  if (mode === 'new') {
    validationSchema = Yup.object().shape({
      first_name: Yup.string()
        .required('Required')
        .min(2, 'Min length is 2 character'),
      mobile: Yup.string().min(10, 'Min 10 digits').required('Required'),
      user_name: Yup.string().required('Required'),
      user_type: Yup.string().required('Required'),
      email: Yup.string().email().required('Required'),
      password: Yup.string()
        .min(6, 'Must be at least 6 characters')
        .max(255)
        .required('Required'),
      confirmPassword: Yup.string()
        .required('Required')
        .when('password', {
          is: (val) => !!(val && val.length > 0),
          then: Yup.string().oneOf(
            [Yup.ref('password')],
            'Passwords do not match'
          )
        })
    })
  } else {
    validationSchema = Yup.object().shape({
      first_name: Yup.string()
        .required('Required')
        .min(2, 'Min length is 2 character'),
      email: Yup.string().email().required('Required'),
      mobile: Yup.string().min(10, 'Min 10 digits').required('Required'),
      user_name: Yup.string().required('Required'),
      user_type: Yup.string().required('Required')
    })
    showOTPui = true
  }

  const handleResendOTP = (mobile) => {
    setOTP('')
    onResendOTP(mobile)
  }

  const username = getUserNameFromToken()
  const userType = getUserType()
  const { outletFetchData, outletFetchError, outletFetchLoading } =
    useFetchOutlet()
  const [selectedOutlets, setSelectedOutlets] = useState([])
  const [initialOutlets, setInitialOutlets] = useState([])
  const handleChangeEvent = (event) => {
    const {
      target: { value }
    } = event
    if (mode !== 'new' && initialValues?.user_type !== 'User') {
      let mergedOutlets = []
      if (initialOutlets.length > 0) {
        value.forEach((id) => {
          const selectedItem = initialOutlets?.find((item) => item._id === id)
          if (selectedItem) {
            mergedOutlets.push(selectedItem)
          }
        })
      }
      if (value.length > 0) {
        value.forEach((id) => {
          const selectedItem = outletFetchData?.data?.find(
            (item) => item._id === id
          )
          if (selectedItem) {
            mergedOutlets.push(selectedItem)
          }
        })
      }
      mergedOutlets = mergedOutlets.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t._id === item._id)
      )
      setInitialOutlets(mergedOutlets)
    }
    setSelectedOutlets(typeof value === 'string' ? value.split(',') : value)
  }

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setStatus, setSubmitting }
  ) => {
    if (!showOTPui) setOTP('')
    else values.OTP = OTP

    if (values.user_type === 'User') {
      if (!selectedOutlets) {
        setErrors({ outlet: 'Required' })
        return
      }
      values.outlet = [selectedOutlets]
    } else {
      if (selectedOutlets.length === 0 && showOutletSelection) {
        setErrors({ outlet: 'Required' })
        return
      }
      values.outlet = selectedOutlets
    }
    onSubmit(values)
  }

  useEffect(() => {
    setInitialOutlets([])
    setSelectedOutlets([])
    if (initialValues?.outlet?.length > 0) {
      if (initialValues?.user_type === 'User') {
        setSelectedOutlets(initialValues.outlet[0]._id)
        setInitialOutlets(initialValues.outlet)
      } else {
        setSelectedOutlets(initialValues.outlet.map((item) => item._id))
        setInitialOutlets(initialValues.outlet)
      }
    }
  }, [initialValues.outlet])

  return (
    <>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={visible}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {title}
          <IconButton
            component="span"
            size="small"
            sx={{ ':hover': { backgroundColor: 'red', color: 'white' } }}
            onClick={() => {
              setSelectedOutlets([])
              onClose()
            }}
            variant="contained"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Container sx={{ marginBottom: 5 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              status
            }) => (
              <form onSubmit={handleSubmit}>
                <Divider my={2} />
                <Card>
                  <CardContent>
                    <Grid container spacing={5} style={{ paddingTop: 10 }}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="first_name"
                          label="First Name"
                          value={values?.first_name}
                          error={Boolean(
                            touched.first_name && errors.first_name
                          )}
                          fullWidth
                          helperText={touched.first_name && errors.first_name}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          disabled={showOTPui && mode === 'new'}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="last_name"
                          label="Last Name"
                          value={values?.last_name}
                          error={Boolean(touched.last_name && errors.last_name)}
                          fullWidth
                          helperText={touched.last_name && errors.last_name}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          disabled={showOTPui && mode === 'new'}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="user_name"
                          label="User ID"
                          value={values?.user_name}
                          error={Boolean(touched.user_name && errors.user_name)}
                          fullWidth
                          helperText={touched.user_name && errors.user_name}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          disabled={mode != 'new'}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="email"
                          label="Email"
                          value={values?.email}
                          error={Boolean(touched.email && errors.email)}
                          fullWidth
                          helperText={touched.email && errors.email}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          type="email"
                          variant="outlined"
                          disabled={mode != 'new'}
                          my={2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                      {mode != 'new' && (
                        <Grid item md>
                          <TextField
                            size="small"
                            name="mobile"
                            label="Mobile"
                            value={values?.mobile}
                            error={Boolean(touched.mobile && errors.mobile)}
                            fullWidth
                            helperText={touched.mobile && errors.mobile}
                            onBlur={handleBlur}
                            margin="dense"
                            onChange={handleChange}
                            variant="outlined"
                            disabled={
                              mode != 'new' || (mode == 'new' && showOTPui)
                            }
                            my={2}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    component="span"
                                    size="small"
                                    color="primary"
                                    onClick={onMobileEdit}
                                    variant="contained"
                                  >
                                    <Edit />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          ></TextField>
                        </Grid>
                      )}
                      {mode == 'new' && (
                        <Grid item md>
                          <TextField
                            size="small"
                            name="mobile"
                            label="Mobile"
                            value={values?.mobile}
                            error={Boolean(touched.mobile && errors.mobile)}
                            fullWidth
                            helperText={touched.mobile && errors.mobile}
                            onBlur={handleBlur}
                            margin="dense"
                            onChange={handleChange}
                            variant="outlined"
                            disabled={
                              mode != 'new' || (mode == 'new' && showOTPui)
                            }
                            my={2}
                          ></TextField>
                        </Grid>
                      )}
                      <Grid item md>
                        {values?.user_type !== 'FOwner' ||
                        userType !== 'FOwner' ? (
                          <TextField
                            size="small"
                            label="User Type"
                            id="user_type"
                            name="user_type"
                            fullWidth
                            margin="dense"
                            value={values?.user_type}
                            onChange={(e) => {
                              handleChange(e)
                              if (e.target.value === 'User') {
                                setSelectedOutlets('')
                              } else
                                setSelectedOutlets(
                                  mode === 'new'
                                    ? []
                                    : initialOutlets.map((item) => item._id)
                                )
                            }}
                            onBlur={handleBlur}
                            disabled={
                              (showOTPui && mode === 'new') ||
                              forFranchise ||
                              (initialOutlets.filter(
                                (item1) =>
                                  !outletFetchData?.data?.some(
                                    (item2) => item1._id === item2._id
                                  )
                              ).length > 0 &&
                                mode !== 'new')
                            }
                            variant="outlined"
                            required
                            select
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            <MenuItem key={'Admin'} value={'Admin'}>
                              Admin
                            </MenuItem>
                            <MenuItem key={'User'} value={'User'}>
                              Cashier
                            </MenuItem>
                            {userType !== 'FOwner' && userType !== 'Admin' && (
                              <MenuItem key={'FOwner'} value={'FOwner'}>
                                FOwner
                              </MenuItem>
                            )}
                          </TextField>
                        ) : (
                          <TextField
                            size="small"
                            label="User Type"
                            id="user_type"
                            name="user_type"
                            fullWidth
                            margin="dense"
                            value={values?.user_type}
                            disabled
                            variant="outlined"
                          />
                        )}
                      </Grid>
                    </Grid>

                    {mode === 'new' && (
                      <Grid container spacing={6}>
                        <Grid item md={6}>
                          <TextField
                            size="small"
                            name="password"
                            label="Password"
                            value={values?.password}
                            error={Boolean(touched.password && errors.password)}
                            fullWidth
                            helperText={touched.password && errors.password}
                            onBlur={handleBlur}
                            margin="dense"
                            onChange={handleChange}
                            type="password"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                        <Grid item md={6}>
                          <TextField
                            size="small"
                            name="confirmPassword"
                            label="Confirm password"
                            value={values?.confirmPassword}
                            error={Boolean(
                              touched.confirmPassword && errors.confirmPassword
                            )}
                            fullWidth
                            helperText={
                              touched.confirmPassword && errors.confirmPassword
                            }
                            onBlur={handleBlur}
                            margin="dense"
                            onChange={handleChange}
                            type="password"
                            variant="outlined"
                            my={2}
                          />
                        </Grid>
                      </Grid>
                    )}
                    {mode != 'new' && username != values?.user_name && (
                      <Grid container spacing={6}>
                        <Grid item md={6}>
                          <TextField
                            size="small"
                            label="Status"
                            id="account_status"
                            name="account_status"
                            fullWidth
                            margin="dense"
                            value={values?.account_status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={mode === 'new'}
                            variant="outlined"
                            required
                            select
                          >
                            <MenuItem key={'active'} value={'active'}>
                              Active
                            </MenuItem>
                            <MenuItem key={'closed'} value={'closed'}>
                              Inactive
                            </MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    )}
                    {showOutletSelection ? (
                      <Grid
                        pt={2.5}
                        container
                        justifyContent={'center'}
                        spacing={6}
                      >
                        <Grid item md={8}>
                          <FormControl fullWidth error={Boolean(errors.outlet)}>
                            <InputLabel id="outlets-label">Outlets</InputLabel>
                            {values?.user_type !== 'User' ? (
                              <Select
                                labelId="outlets-label"
                                id="outlets-label"
                                multiple
                                disabled={username === values?.user_name}
                                label="Outlets"
                                value={selectedOutlets}
                                onChange={handleChangeEvent}
                                input={<OutlinedInput label="Outlets" />}
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5
                                    }}
                                  >
                                    {selected.map((id) => {
                                      const selectedItem =
                                        mode === 'new' || userType === 'Owner'
                                          ? outletFetchData?.data?.find(
                                              (item) => item._id === id
                                            )
                                          : initialOutlets?.find(
                                              (item) => item._id === id
                                            )
                                      return selectedItem ? (
                                        <Chip
                                          key={id}
                                          label={selectedItem.name}
                                        />
                                      ) : null
                                    })}
                                  </Box>
                                )}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 200
                                    }
                                  }
                                }}
                              >
                                {outletFetchData?.data?.map((value) => (
                                  <MenuItem key={value._id} value={value._id}>
                                    <Grid container direction={'column'}>
                                      <Grid item>
                                        <Typography variant="body1">
                                          {value.name}
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="caption">
                                          {value.sub_business_name}
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Divider />
                                      </Grid>
                                    </Grid>
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Select
                                labelId="outlets-label"
                                id="outlets-label"
                                multiple={false}
                                label="Outlets"
                                value={selectedOutlets || ''}
                                onChange={(e) => {
                                  setSelectedOutlets(e.target.value)
                                }}
                                input={<OutlinedInput label="Outlets" />}
                                renderValue={(selected) => {
                                  const selectedItem =
                                    outletFetchData?.data?.find(
                                      (item) => item._id === selected
                                    )
                                  return selectedItem ? (
                                    <Chip
                                      key={selected}
                                      label={selectedItem.name}
                                    />
                                  ) : null
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 200
                                    }
                                  }
                                }}
                              >
                                {outletFetchData?.data?.map((value) => (
                                  <MenuItem key={value._id} value={value._id}>
                                    <Grid container direction={'column'}>
                                      <Grid item>
                                        <Typography variant="body1">
                                          {value.name}
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="caption">
                                          {value.sub_business_name}
                                        </Typography>
                                      </Grid>
                                      <Grid item>
                                        <Divider />
                                      </Grid>
                                    </Grid>
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                            {errors.outlet && (
                              <Typography variant="caption" color="error">
                                {errors.outlet}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container justifyContent={'center'}>
                        <Grid item md={8}>
                          <TextField
                            size="small"
                            value={outletName}
                            label="Outlet"
                            margin="dense"
                            variant="outlined"
                            disabled
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    )}

                    {showOTPui && mode === 'new' && (
                      <div sx={{ marginTop: 5 }}>
                        <Divider sx={{ marginTop: 5, marginBottom: 1 }} />
                        <Typography gutterBottom component="h2" variant="body1">
                          OTP Verification
                        </Typography>
                        <OTPInput
                          value={OTP}
                          onChange={setOTP}
                          autoFocus
                          OTPLength={4}
                          otpType="number"
                          disabled={false}
                        />
                        <ResendOTP
                          onResendClick={() => {
                            handleResendOTP(values.mobile)
                          }}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <ActionButton
                      variant="outlined"
                      size="medium"
                      type="submit"
                      mt={8}
                      disabled={loading}
                    >
                      <Grid container direction={'row'} spacing={1}>
                        <Grid item>{showOTPui ? okText : 'Send OTP'}</Grid>
                        {loading && (
                          <Grid item>
                            <CircularProgress size={20} />
                          </Grid>
                        )}
                      </Grid>
                    </ActionButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      </Dialog>
    </>
  )
}

export default UserDialog
